import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Globe, Wifi, Cog, CheckCircle2,
  Rocket, TrendingUp, AlertTriangle, Shield,
  Bot, Calendar, MapPin, Search, Monitor, Network, Video, Wrench,
  GitMerge, Server, ClipboardList
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

// Button Style Components
const PrimaryButton = ({ children, className = "", ...props }) => (
  <Button
    className={`bg-gradient-to-r from-[#6B46C1] to-[#4169E1] text-white px-10 py-5 rounded-xl font-semibold shadow-lg hover:from-[#5534A5] hover:to-[#3152B8] hover:-translate-y-0.5 hover:shadow-none transition-all duration-300 ${className}`}
    {...props}
  >
    {children}
  </Button>
);

const SecondaryButton = ({ children, className = "", ...props }) => (
  <Button
    variant="outline"
    className={`bg-transparent border-2 border-[#6B46C1] text-[#6B46C1] px-10 py-5 rounded-xl font-semibold hover:bg-[#F5F0FF] hover:-translate-y-0.5 transition-all duration-300 ${className}`}
    {...props}
  >
    {children}
  </Button>
);

const GradientCard = ({ children, className = "", gradientFrom = "from-blue-500", gradientTo = "to-indigo-500" }) => (
  <div className={`group relative ${className}`}>
    <div className={`absolute -inset-0.5 bg-gradient-to-r ${gradientFrom} ${gradientTo} rounded-2xl opacity-0 group-hover:opacity-100 blur transition-all duration-500`}></div>
    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-indigo-500/20 to-violet-500/20 rounded-2xl opacity-0 group-hover:opacity-50 transition-all duration-500"></div>
    <div className="relative h-full bg-white border border-gray-100 rounded-2xl shadow-lg transition-all duration-300 group-hover:shadow-xl group-hover:border-transparent">
      {children}
    </div>
  </div>
);

const MarketingHome = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cloud-white">
      <SEO 
        title="Las Vegas Technology Solutions - Websites, Automation & Event Tech"
        description="Fearless IT helps Las Vegas businesses grow with smart websites, AI automation, and event technology solutions. Technology + Project Management expertise for businesses ready to scale."
        keywords="Las Vegas web development, Las Vegas AI automation, Las Vegas event technology, Nevada IT services, Las Vegas business technology, convention technology Las Vegas"
        url="/"
        isLocalPage={true}
      />
      
      {/* Navigation */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 sm:py-24 lg:py-32 bg-white" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-indigo-50/50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6 sm:mb-8">
              <Rocket className="h-4 w-4 text-fearless-blue" />
              <span className="text-xs sm:text-sm text-slate-gray font-medium">Trusted by Las Vegas businesses</span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-carbon-black mb-4 sm:mb-6 leading-tight" data-testid="hero-title">
              Technology That Helps Las Vegas Businesses{' '}
              <span className="text-gradient">Grow, Rank, and Run Flawlessly</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-slate-gray mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-4">
              Smart websites, AI automation, and event technology solutions designed to help businesses attract customers, operate efficiently, and scale with confidence.
            </p>
            
            {/* Trust Signals */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-8 sm:mb-10 px-4">
              <div className="flex items-center gap-2 text-sm text-slate-gray">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span>Technology + Project Management expertise</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-gray">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span>Smart websites, automation & event tech</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Link to="/resources/consultation" className="w-full sm:w-auto">
                <PrimaryButton className="w-full sm:w-auto text-base sm:text-lg" data-testid="hero-cta-btn">
                  Get a Free Technology Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </PrimaryButton>
              </Link>
              <Link to="/growth-audit" className="w-full sm:w-auto">
                <SecondaryButton className="w-full sm:w-auto text-base sm:text-lg" data-testid="hero-secondary-btn">
                  Free Growth Audit
                </SecondaryButton>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-20 bg-cloud-white" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-50 border-2 border-red-100 mb-6">
              <AlertTriangle className="h-8 w-8 sm:h-10 sm:w-10 text-red-500" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-carbon-black mb-6">
              Most Businesses Are Losing Customers Because Their Technology Isn't Working.
            </h2>
            <div className="text-left space-y-4 text-base sm:text-lg text-slate-gray leading-relaxed px-4">
              <p>
                Many businesses struggle with:
              </p>
              <ul className="space-y-3 pl-4">
                <li className="flex items-start">
                  <span className="text-red-500 mr-3">•</span>
                  <span>Outdated or missing websites</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3">•</span>
                  <span>Poor Google visibility</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3">•</span>
                  <span>Manual processes that waste time</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3">•</span>
                  <span>Event technology failures during conferences</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3">•</span>
                  <span>Disconnected systems that slow down growth</span>
                </li>
              </ul>
              <p className="font-semibold text-carbon-black pt-4">
                When technology is confusing or unreliable, it costs your business time, money, and opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Guide Section */}
      <section className="py-16 sm:py-20 bg-white" data-testid="guide-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-fearless-blue via-electric-indigo to-deep-violet p-0.5 mb-6">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-fearless-blue" />
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-carbon-black mb-6">
              Meet Fearless IT
            </h2>
            <p className="text-base sm:text-lg text-slate-gray leading-relaxed mb-6 px-4">
              Fearless IT helps businesses design smarter technology systems.
            </p>
            <p className="text-base sm:text-lg text-slate-gray leading-relaxed mb-6 px-4">
              Led by experienced technology leaders with backgrounds in infrastructure, project management, and digital strategy, we build solutions that actually support business growth.
            </p>
            <p className="text-base sm:text-lg text-slate-gray leading-relaxed px-4">
              Instead of juggling multiple tools and vendors, we create integrated systems that help your organization operate with clarity and efficiency.
            </p>
          </div>
        </div>
      </section>

      {/* Service Divisions Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-carbon-black mb-3 sm:mb-4" data-testid="services-title">
              Our Service Divisions
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Fearless IT Digital */}
            <GradientCard gradientFrom="from-blue-500" gradientTo="to-indigo-500" data-testid="service-card-digital">
              <div className="h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-2xl"></div>
              <div className="p-6 sm:p-8">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl gradient-fearless flex items-center justify-center mb-4 sm:mb-6">
                  <Globe className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-carbon-black mb-2">Fearless IT Digital</h3>
                <p className="text-sm text-[#6B46C1] font-semibold mb-4">Smart Websites, Applications & Growth Systems</p>
                <p className="text-slate-gray leading-relaxed mb-4 text-sm sm:text-base">
                  We build modern digital platforms that help businesses attract customers and convert online traffic.
                </p>
                <p className="text-sm text-slate-gray font-medium mb-3">Services include:</p>
                <ul className="space-y-2 text-slate-gray text-sm mb-6">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Smart website design</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>AI chatbots and automation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Booking and scheduling systems</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Google Business optimization</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Local SEO strategy</span>
                  </li>
                </ul>
                <Link to="/services/digital">
                  <Button className="w-full gradient-fearless text-white">
                    Launch My Website
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </GradientCard>

            {/* Fearless IT Events */}
            <GradientCard gradientFrom="from-violet-500" gradientTo="to-purple-500" data-testid="service-card-events">
              <div className="h-1.5 bg-gradient-to-r from-violet-500 to-purple-500 rounded-t-2xl"></div>
              <div className="p-6 sm:p-8">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center mb-4 sm:mb-6">
                  <Wifi className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-carbon-black mb-2">Fearless IT Events</h3>
                <p className="text-sm text-[#6B46C1] font-semibold mb-4">Convention Technology & Event Infrastructure</p>
                <p className="text-slate-gray leading-relaxed mb-4 text-sm sm:text-base">
                  Las Vegas hosts some of the world's largest conferences and events. We provide the technology that keeps them running smoothly.
                </p>
                <p className="text-sm text-slate-gray font-medium mb-3">Services include:</p>
                <ul className="space-y-2 text-slate-gray text-sm mb-6">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-violet-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Conference Wi-Fi networks</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-violet-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Digital displays and screens</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-violet-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Livestream systems</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-violet-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Check-in and registration tech</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-violet-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Event troubleshooting and support</span>
                  </li>
                </ul>
                <Link to="/services/events">
                  <Button className="w-full bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:from-violet-600 hover:to-purple-600">
                    Request Event Tech Support
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </GradientCard>

            {/* Fearless IT Systems */}
            <GradientCard gradientFrom="from-amber-500" gradientTo="to-orange-500" data-testid="service-card-systems">
              <div className="h-1.5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-2xl"></div>
              <div className="p-6 sm:p-8">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center mb-4 sm:mb-6">
                  <Cog className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-carbon-black mb-2">Fearless IT Systems</h3>
                <p className="text-sm text-[#6B46C1] font-semibold mb-4">Automation & Technology Consulting</p>
                <p className="text-slate-gray leading-relaxed mb-4 text-sm sm:text-base">
                  For businesses that need advanced solutions, we provide strategic consulting and automation systems.
                </p>
                <p className="text-sm text-slate-gray font-medium mb-3">Services include:</p>
                <ul className="space-y-2 text-slate-gray text-sm mb-6">
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Workflow automation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>System integrations</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>IT infrastructure consulting</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Project and change management guidance</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="h-4 w-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Digital transformation strategy</span>
                  </li>
                </ul>
                <Link to="/services/systems">
                  <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600">
                    Schedule a Strategy Call
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </GradientCard>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-20 bg-white" data-testid="plan-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-carbon-black mb-3 sm:mb-4">
              A Simple 3-Step Plan
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Step 1 */}
            <GradientCard gradientFrom="from-blue-400" gradientTo="to-blue-600" className="h-full">
              <div className="p-6 sm:p-8 h-full">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full gradient-fearless text-white font-bold text-lg sm:text-xl mb-4">
                  1
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-carbon-black mb-2">Discover</h3>
                <p className="text-slate-gray text-sm sm:text-base leading-relaxed">
                  We learn about your business goals, technology challenges, and opportunities.
                </p>
              </div>
            </GradientCard>

            {/* Step 2 */}
            <GradientCard gradientFrom="from-indigo-400" gradientTo="to-indigo-600" className="h-full">
              <div className="p-6 sm:p-8 h-full">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 text-white font-bold text-lg sm:text-xl mb-4">
                  2
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-carbon-black mb-2">Build</h3>
                <p className="text-slate-gray text-sm sm:text-base leading-relaxed">
                  We design and implement websites, automation systems, or event technology solutions.
                </p>
              </div>
            </GradientCard>

            {/* Step 3 */}
            <GradientCard gradientFrom="from-violet-400" gradientTo="to-violet-600" className="h-full sm:col-span-2 lg:col-span-1">
              <div className="p-6 sm:p-8 h-full">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold text-lg sm:text-xl mb-4">
                  3
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-carbon-black mb-2">Scale</h3>
                <p className="text-slate-gray text-sm sm:text-base leading-relaxed">
                  We optimize your systems so your business can grow with clarity and confidence.
                </p>
              </div>
            </GradientCard>
          </div>

          <div className="text-center mt-10">
            <Link to="/contact">
              <PrimaryButton className="text-base sm:text-lg">
                Start My Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </PrimaryButton>
            </Link>
          </div>
        </div>
      </section>

      {/* Results / Benefits Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-green-50 to-emerald-50" data-testid="success-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-fearless-blue via-electric-indigo to-deep-violet p-0.5 mb-6">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 text-fearless-blue" />
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-carbon-black mb-6">
              Technology That Works So You Can Focus on Your Business.
            </h2>
            
            <div className="text-left mb-8 px-4">
              <p className="text-base sm:text-lg text-slate-gray mb-4">When your systems are structured correctly:</p>
              <ul className="space-y-3 text-base sm:text-lg text-slate-gray">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>Customers can find you online</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>Your team works more efficiently</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>Events run smoothly</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <span>Your business can scale with confidence</span>
                </li>
              </ul>
            </div>

            <p className="text-lg sm:text-xl font-bold text-gradient">
              Fearless IT builds the technology infrastructure that makes growth possible.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[#6B46C1]/5 to-[#4169E1]/5" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-carbon-black mb-4">
            Ready to Build Smarter Technology Systems?
          </h2>
          
          <p className="text-base sm:text-lg text-slate-gray mb-8 sm:mb-10 max-w-2xl mx-auto px-4">
            Whether you need a website, automation tools, or convention technology support, Fearless IT can help.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link to="/resources/consultation" className="w-full sm:w-auto">
              <PrimaryButton className="w-full sm:w-auto text-base sm:text-lg">
                Schedule a Free Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </PrimaryButton>
            </Link>
            <Link to="/company/about" className="w-full sm:w-auto">
              <SecondaryButton className="w-full sm:w-auto text-base sm:text-lg">
                About Fearless IT
              </SecondaryButton>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
};

export default MarketingHome;
